import { DateTime } from 'luxon';
import { getSelectNewOrderEntityState, fetchAndParseEntity } from './orders-dialog-context-utilities';
import { getSitesBarnsAndPhysicalBins, getSelectedDateData } from './orders-dialog-context-get-actions';
import { fetchFeeds } from '~/feeds/feed-helpers';

export async function initializeBarn ({ barnId }, { state, dispatch }) {
  try {
    if (!barnId) {
      return;
    }

    const { barns: stateBarns, siteEntity: stateSiteEntity } = state;
    const barnEntity = stateBarns?.[barnId]?.barnEntity || await fetchAndParseEntity(barnId);
    const siteId = barnEntity.getLinkByRel('https://api.binsentry.com/rel/farm').href;
    const siteEntity = stateSiteEntity?.getLinkByRel('self').href === siteId ? stateSiteEntity : await fetchAndParseEntity(siteId);
    const { barns, feeds, hasSuggestions } = siteEntity === stateSiteEntity ? state : await loadDataForSiteEntity(siteEntity);

    const selectedBarn = barns[barnId];
    const data = {
      selectedBarnId: barnId,
      selectedBarnsBins: selectedBarn.binEntities || [],
      selectedBarnEntity: selectedBarn.barnEntity,
      selectedBarnOrdersEntity: selectedBarn.ordersEntity,
      selectedBarnOrganizationBinEntity: selectedBarn.organizationBinEntity,
      barns,
      siteEntity,
      feeds,
      hasSuggestions,
    };

    dispatch({
      type: 'initialize-barn-data',
      data,
    });

    return data;
  } catch (e) {
    console.error(e);
    dispatch({ type: 'set-initialization-error-message', initializationErrorMessage: e?.message });
  }
}

async function loadDataForSiteEntity(siteEntity) {
  const [{ barns, hasSuggestions }, feeds] = await Promise.all([
    loadBarnsAndHasSuggestionsForSiteEntity(siteEntity),
    loadFeedsForSiteEntity(siteEntity),
  ]);

  return { feeds, barns, hasSuggestions };
}

async function loadBarnsAndHasSuggestionsForSiteEntity(siteEntity) {
  const {
    barnEntities,
    binEntitiesByBarnId,
    organizationBinEntitiesByBarnId,
    orderEntitiesByBarnId,
    hasSuggestions,
  } = await getSitesBarnsAndPhysicalBins(siteEntity);
  const barns = Object.fromEntries(barnEntities.map(barnEntity => {
    const barnId = barnEntity.getLinkByRel('self').href;
    return [
      barnId,
      {
        barnEntity,
        binEntities: binEntitiesByBarnId[barnId],
        organizationBinEntity: organizationBinEntitiesByBarnId[barnId],
        ordersEntity: orderEntitiesByBarnId[barnId],
      },
    ];
  }));

  return { barns, hasSuggestions };
}

async function loadFeedsForSiteEntity(siteEntity) {
  const feedsEntity = await fetchAndParseEntity(siteEntity.getSubEntityByRel('https://api.binsentry.com/rel/v2/feeds').href);
  return fetchFeeds(feedsEntity);
}

export async function initializeContext ({
  initialIsoDate,
  initialSelectedOrderId,
  initialBarnId,
  initialFeedId,
  initialBinAllocations,
  onOrdersUpdate,
  onOrderClose,
  onDialogClose,
  enqueueSnackbar,
}, { dispatch, state }) {
  try {
    dispatch({ type: 'start-initialization' });
    const usersCurrentLocalIsoDate = new Date().toISOString();

    // Fetch whatever initial entity we were given by id
    let selectedOrderEntity;
    let isoDate = initialIsoDate;
    if (initialSelectedOrderId) {
      selectedOrderEntity = await fetchAndParseEntity(initialSelectedOrderId);
      isoDate = initialIsoDate || selectedOrderEntity?.properties?.deliveryDate;
    }

    // Fetch the associated barn
    const barnId = selectedOrderEntity?.getLinkByRel('https://api.binsentry.com/rel/barn')?.href || state.selectedBarnId || initialBarnId;
    const { barns, siteEntity, selectedBarnOrganizationBinEntity } = await initializeBarn({ barnId }, { state, dispatch });

    const initialDateNoon = DateTime.fromISO(isoDate).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
    const isoDateNoon = initialDateNoon.toISO();
    const isoDateNoonUTC = initialDateNoon.toUTC().toISO();

    dispatch({
      type: 'set-initialization-data',
      data: {
        isoDate,
        isoDateNoon,
        isoDateNoonUTC,
        selectedBarnId: barnId,
        selectedOrderEntity,
        specifyingNewOrder: !selectedOrderEntity,
        usersCurrentLocalIsoDate,
        onOrdersUpdate,
        onOrderClose,
        onDialogClose,
        enqueueSnackbar,
      },
    });

    const {
      forecastsByBinId,
      binLevelsByBinId,
      feedOrdersEntity,
      daysOrders,
      daysAllocations,
      barnBinLevelEntities,
    } = await getSelectedDateData({ isoDate, barns, siteEntity, includeLevels: true });

    const selectedBarnOrganizationBinId = selectedBarnOrganizationBinEntity.getLinkByRel('self').href;
    const selectedBarnOrganizationBinLevelsEntity = barnBinLevelEntities.find(levelEntity => levelEntity.getSubEntityByRel('https://api.binsentry.com/rel/bin').href === selectedBarnOrganizationBinId);

    await dispatch({
      type: 'complete-initialization',
      data: {
        selectedBarnOrganizationBinLevelsEntity,
        selectedOrderId: initialSelectedOrderId,
        forecastsByBinId,
        binLevelsByBinId,
        feedOrdersEntity,
        daysOrders,
        daysAllocations,
        barnBinLevelEntities,
      },
    });

    if (initialFeedId) {
      const feedId = selectedOrderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href;
      if (feedId !== initialFeedId) {
        // Only set unsavedChanges when needed, otherwise user is prompted about unsaved changes on close
        await dispatch({
          type: 'set-unsaved-change-data',
          data: { feedId: initialFeedId },
        });
      }
    }

    if (initialBinAllocations && Object.keys(initialBinAllocations).length > 0) {
      const selectedOrderBinAllocations = selectedOrderEntity?.getSubEntitiesByRel('https://api.binsentry.com/rel/order-bin-allocation');
      const initialUnsavedBinAllocations = Object.fromEntries(Object.entries(initialBinAllocations).map(([binId, { volume } ]) => {
        const selectedOrderAllocation = selectedOrderBinAllocations?.find(allocation => allocation.getLinkByRel('https://api.binsentry.com/rel/bin').href === binId);
        if (!selectedOrderAllocation || selectedOrderAllocation.properties.volume !== volume) {
          return [`binAllocations[${binId}]`, volume];
        }
      }).filter(item => item));
      if (Object.keys(initialUnsavedBinAllocations).length > 0) {
        // Only set unsavedChanges when needed, otherwise user is prompted about unsaved changes on close
        await dispatch({
          type: 'set-unsaved-change-data',
          data: { ...initialUnsavedBinAllocations },
        });
      }
    }
  } catch (e) {
    console.error(e);
    dispatch({ type: 'set-initialization-error-message', initializationErrorMessage: e?.message });
  }
}

export const initializeDate = async ({ state, action }) => {
  const {
    barns,
    siteEntity,
    selectedBarnOrganizationBinEntity,
  } = state;

  const { isoDate, includeLevels = true } = action;

  if (!isoDate) {
    // Date was cleared
    return {
      ...state,
      isoDate: null,
      isoDateNoon: null,
      isoDateNoonUTC: null,
      selectedBarnOrganizationBinLevelsEntity: null,
      forecastsByBinId: {},
      binLevelsByBinId: {},
      daysOrders: [],
      daysAllocations: [],
      barnBinLevelEntities: [],
    };
  }

  const response = await getSelectedDateData({
    isoDate,
    barns,
    siteEntity,
  });

  const { barnBinLevelEntities } = response;
  const selectedBarnOrganizationBinId = selectedBarnOrganizationBinEntity.getLinkByRel('self').href;
  const selectedBarnOrganizationBinLevelsEntity = barnBinLevelEntities.find(levelEntity => levelEntity.getSubEntityByRel('https://api.binsentry.com/rel/bin').href === selectedBarnOrganizationBinId);

  const updatedState = {
    ...state,
    isoDate,
    ...includeLevels ? { selectedBarnOrganizationBinLevelsEntity } : {},
    ...response,
  };

  return {
    ...updatedState,
    ...!Object.keys(updatedState.unsavedChanges).includes('deliveryDate') ? getSelectNewOrderEntityState({ state: updatedState }) : {},
  };
};
